import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import ContactForm from '../components/ContactForm';
import GallerySection from '../components/GallerySection';
import { getCdnImage } from '../lib/imageUtils';

export default function ServiceWater() {
  const serviceItems = [
    {
      title: 'Akut hjælp ved ledningsbrud og rørskader',
      desc: 'Hurtig udrykning og præcis lokalisering og udbedring af akutte vandledningsbrud for at minimere vandspild og gener for forbrugerne.',
      icon: 'fa-solid fa-triangle-exclamation',
    },
    {
      title: 'Nedgravning og udskiftning af målerbrønde',
      desc: 'Professionel nedgravning og montering af moderne målerbrønde efter gældende forsyningskrav og standarder.',
      icon: 'fa-solid fa-circle-dot',
    },
    {
      title: 'Afpropning af stikledninger',
      desc: 'Sikker afpropning og sanering af ubenyttede stikledninger for at sikre ledningsnettets hygiejne og tæthed.',
      icon: 'fa-solid fa-ban',
    },
    {
      title: 'Skydning med jordraket',
      desc: 'Minimal opgravning under veje, belægninger og haver via skydning med jordraket – skånsomt og effektivt for omgivelserne.',
      icon: 'fa-solid fa-arrows-to-dot',
    },
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      <SeoMeta
        title="Vandværksservice & ledningsarbejde - ENTREPRENØR LANG ApS"
        description="Professionelt gravearbejde og vedligeholdelse for vandværker. Akut hjælp ved ledningsbrud og rørskader, målerbrønde og jordraket."
        keywords="vandværksservice, ledningsbrud, rørskader, målerbrønde, jordraket, Lolland, Falster, Øster Ulslev"
      />

      {/* Hero */}
      <section className="relative bg-slate-950 text-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img
            src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/entreprenor-vandvaerk-ledningsarbejde.jpg', {
              width: 1920,
              quality: 80,
            })}
            alt="Vandværksservice gravearbejde"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors uppercase tracking-wider"
            >
              <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
              <span>Tilbage til alle ydelser</span>
            </Link>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
              Vandværksservice & ledningsarbejde
            </h1>
            <p className="text-lg text-slate-200 leading-relaxed font-medium">
              Professionelt gravearbejde og vedligeholdelse for vandværker.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <a
                href="#kontakt"
                className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow transition-all"
              >
                Få et tilbud
              </a>
              <a
                href="tel:40309708"
                className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-sm border border-white/20 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone mr-2 text-emerald-400" aria-hidden="true" />
                Ring 40 30 97 08
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Specs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-6">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-2">
                <i className="fa-solid fa-droplet text-xs" aria-hidden="true" />
                Specialiseret vandværksservice
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Pålidelig partner til vandværker og forsyninger
              </h2>
              <p className="text-slate-600 text-base mt-3 leading-relaxed">
                Hos ENTREPRENØR LANG ApS har vi mange års erfaring som fast samarbejdspartner for lokale vandværker. Vi rykker hurtigt ud med det nødvendige materiel, uanset om der er tale om planlagt ledningsfornyelse eller akutte ledningsbrud.
              </p>
            </div>

            {/* Scrape verbatim excerpt */}
            <div className="p-5 rounded-xl bg-slate-100 border-l-4 border-emerald-600 text-slate-700 text-sm leading-relaxed">
              <p className="font-semibold text-slate-900 mb-1">Fra virksomhedens profil:</p>
              <p>
                ”...der er blevet meget gravearbejde for vandværker ved brud, skiftning af målerbrønde, afpropning af stik osv. Når vi ikke graver målerbrønde ned og skifter anboringer, laver vi andre serviceydelser for vores vand-kunder.”
              </p>
            </div>

            {/* Service Point Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              {serviceItems.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-2"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center">
                    <i className={`${item.icon} text-base`} aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-base leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right side visual: Real Facebook trench image */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200 bg-white">
              <img
                src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-605221099-1366670058493953-1062181551945056561-n.jpg', {
                  width: 800,
                  quality: 85,
                })}
                alt="Blåt vandrør med messingfittings i jordrende til vandværksservice"
                className="w-full h-80 object-cover"
              />
              <div className="p-5">
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
                  Projektbillede fra marken
                </span>
                <p className="text-slate-900 font-bold text-base mt-1">
                  Udgravning og fremføring af forsyningsledninger
                </p>
                <p className="text-slate-600 text-xs sm:text-sm mt-1">
                  Udført med stor omhyggelighed for at beskytte det eksisterende forsyningsnet.
                </p>
              </div>
            </div>

            <div className="bg-emerald-900 text-white p-6 rounded-2xl space-y-4">
              <h4 className="font-extrabold text-lg flex items-center gap-2">
                <i className="fa-solid fa-bell text-emerald-400" aria-hidden="true" />
                Akut ledningsbrud?
              </h4>
              <p className="text-emerald-100 text-sm">
                Ved mistanke om rørbrud eller akut vandspild kan du ringe direkte på vores vagttelefon.
              </p>
              <a
                href="tel:40309708"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-slate-900 font-bold text-sm hover:bg-slate-100 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-emerald-700" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring 40 30 97 08</span>
              </a>
            </div>
          </div>
        </div>

        {/* Dynamic CMS Gallery Section connected by slug */}
        <GallerySection
          gallerySlug="vandvaerksservice"
          title="Billeder fra vandværksarbejde"
          subtitle="Se eksempler på vores arbejde med ledninger, brønde og udgravninger."
        />
      </section>

      {/* Form Section */}
      <section id="kontakt" className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContactForm defaultService="Vandværksservice & ledningsarbejde" />
        </div>
      </section>
    </div>
  );
}
